import {
  type MutateOptions,
  type MutationFunction,
  type UseMutationResult,
  // eslint-disable-next-line @web-monorepo/no-use-raw-onMutate
  useMutation as useMutationRQ,
} from "@tanstack/react-query";
import { useCallback } from "react";

// 1. Strict options type
type MutateOptionsStrict<TData, TError, TVariables, TContext> = Omit<
  MutateOptions<TData, TError, TVariables, TContext>,
  "onError"
> & {
  onError: (error: TError, variables: TVariables, context: TContext | undefined) => unknown;
};

// 2. Strict result type
export type UseMutationStrict<TData, TError, TVariables, TContext> = Omit<
  UseMutationResult<TData, TError, TVariables, TContext>,
  "mutate" | "mutateAsync"
> & {
  mutate: (variables: TVariables, options: MutateOptionsStrict<TData, TError, TVariables, TContext>) => void;
  mutateAsync: (
    variables: TVariables,
    options: MutateOptionsStrict<TData, TError, TVariables, TContext>,
  ) => Promise<TData>;
};

// Annoying type coercion to make react-query happy but keep strict options in our hook
const getSafeOptions = <TData, TError, TVariables, TContext>(
  opts: MutateOptionsStrict<TData, TError, TVariables, TContext>,
) => {
  // opts is sometimes undefined, but everywhere this happens, we are failing type checks, so should burn down the need for {} over time
  opts = opts || {};
  return {
    ...opts,
    onError: opts.onError as (err: unknown, vars: unknown, ctx: unknown) => void,
    onSuccess: opts.onSuccess as ((data: unknown, vars: unknown, ctx: unknown) => void) | undefined,
    onSettled: opts.onSettled as ((data: unknown, error: unknown, vars: unknown, ctx: unknown) => void) | undefined,
  };
};

// 3. Wrapper hook that enforces strict options for mutate
const useStrictMutation = <TData = unknown, TError = unknown, TVariables = void, TContext = unknown>(
  mutationFn: MutationFunction<TData, TVariables>,
  options?: MutateOptionsStrict<TData, TError, TVariables, TContext>,
): UseMutationStrict<TData, TError, TVariables, TContext> => {
  const {
    mutate: _mutate,
    mutateAsync: _mutateAsync,
    ...rest
  } = useMutationRQ({
    // ✅ This disambiguates the call
    mutationFn,
    ...(options as object),
  });

  return {
    ...rest,
    mutate: useCallback(
      (variables, opts) => {
        const safeOpts = getSafeOptions(opts);
        return _mutate(variables, safeOpts);
      },
      [_mutate],
    ),
    mutateAsync: useCallback(
      (variables, opts) => {
        const safeOpts = getSafeOptions(opts);
        return _mutateAsync(variables, safeOpts);
      },
      [_mutateAsync],
    ),
  } as UseMutationStrict<TData, TError, TVariables, TContext>;
};

const useMutation = <TData = unknown, TError = unknown, TVariables = void, TContext = unknown>(
  mutationFn: MutationFunction<TData, TVariables>,
  options?: MutateOptionsStrict<TData, TError, TVariables, TContext>,
): UseMutationStrict<TData, TError, TVariables, TContext> => {
  return useStrictMutation(mutationFn, options);
};

export default useMutation;
